import * as React from 'react';
import InputHandler from 'Components/InputHandler';
import { fm } from 'Components/FormatMessage';
import type { IExtendedSearchField } from '../ExtendedSearchField';
import type { TESSearchFields } from '../../definitions';
import { extendedSearchFields } from '../../config';
import { capitalize } from 'Utility/string';

const DefaultField = ({ platform, searchFields, field, onChange, value }: IOwnProps): React.JSX.Element => {
  const thisField = platform
    ? extendedSearchFields()[platform].find(esField => esField.name === field.name)
    : undefined;

  const handleChange = (values: TOnChangeValues): void => {
    const thisName = Object.keys(values)[0];
    if (!thisName) return;
    const thisValue = values[thisName];

    switch (field.type) {
      // RangeSlider values are number arrays which are mapped to min-max objects
      case 'RangeSlider': {
        // If the input value is not a number array, just set to noCropping
        if (!thisValue || !Array.isArray(thisValue) || !thisValue.every(v => typeof v === 'number')) {
          onChange({ [field.name]: 'noCropping' });
          return;
        }

        let setToNoCropping = false;
        let fieldSuffix = capitalize(thisName);

        const min = thisField?.min;
        const max = thisField?.max;

        // Handle exceptions / special cases
        switch (field.name) {
          case 'age':
          case 'latestBMI':
            setToNoCropping = thisValue[0] === min && thisValue[1] === max;
            if (field.name === 'latestBMI') fieldSuffix = 'BMI';
            break;
          case 'latestMgcScore':
          case 'latestHcpReportedMgadlScore':
          case 'latestPatientReportedMgadlScore':
            fieldSuffix = 'Score';
            break;
          default:
            break;
        }

        onChange({
          [field.name]: setToNoCropping
            ? 'noCropping'
            : { [`min${fieldSuffix}`]: thisValue[0] as number, [`max${fieldSuffix}`]: thisValue[1] as number },
        });
        break;
      }
      // Default
      default:
        // Handle exceptions / special cases
        switch (field.name) {
          case 'diagnosis':
            if (
              platform === 'ms' &&
              (!Array.isArray(thisValue) || !thisValue.includes('ms')) &&
              searchFields.typeOfDisease !== 'noCropping'
            ) {
              onChange({
                diagnosis: thisValue,
                typeOfDisease: 'noCropping',
              });
            } else {
              onChange({ diagnosis: thisValue });
            }
            break;
          default:
            onChange(values);
            break;
        }
        break;
    }
  };

  return (
    <div style={{ paddingRight: field.type === 'RangeSlider' ? '4.2rem' : undefined }}>
      <InputHandler
        type={field.type as never}
        editing={true}
        name={field.name}
        formData={{
          onChange: handleChange,
          document: {
            [field.name]:
              value === 'noCropping'
                ? field.type === 'Checkbox'
                  ? []
                  : undefined
                : field.type === 'RangeSlider'
                  ? [Object.values(value as TAnyObject)[0], Object.values(value as TAnyObject)[1]]
                  : value,
          },
        }}
        options={field.options}
        option={field.option}
        optionFormatter={
          field.optionFormatter ??
          ((name: string | number): string | React.JSX.Element => fm(`extendedSearch.opts.${name}`))
        }
        min={field.min}
        max={field.max}
        tooltips={field.tooltips}
        style={field.style}
        marks={field.marks}
        showRangeText={field.showRangeText}
        placeholder={field.placeholder}
        showLabel={field.showLabel}
      />
    </div>
  );
};

interface IOwnProps extends Pick<IExtendedSearchField, 'platform' | 'searchFields' | 'field' | 'onChange'> {
  value: TESSearchFields[keyof TESSearchFields];
}

export default DefaultField;
